const trimTrailingSlash = (url: string) => url.replace(/\/$/, '');

/** 开发环境浏览器经常连不上这些图床，展示时改走本地代理 */
const DEV_IMAGE_PROXY_HOSTS = [
    'static.actqa.com',
    'static-images.actqa.com',
    'static.story.fun',
    'static.socrates.com',
    'dev-video.actqa.com',
];

const IMAGE_PATH = /\.(png|jpe?g|webp|gif|avif)$/i;

export const isProxyableMiniDramaImage = (url: string) => {
    try {
        const parsed = new URL(url);
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false;
        if (!IMAGE_PATH.test(parsed.pathname)) return false;
        const host = parsed.hostname;
        if (host === 'amazonaws.com' || host.endsWith('.amazonaws.com')) return true;
        return DEV_IMAGE_PROXY_HOSTS.includes(host);
    } catch {
        return false;
    }
};

/** 仅 dev 展示用。写入配置的地址保持原样 */
export const resolveDevImageDisplayUrl = (url?: string) => {
    if (!url) return '';
    if (!import.meta.env.DEV || import.meta.env.VITEST) return url;
    if (!isProxyableMiniDramaImage(url)) return url;
    return `/__mini_drama_asset?url=${encodeURIComponent(url)}`;
};

/** S3 桶直链：上传后即时预览、与预签名 PUT 目标一致 */
export const getMiniDramaUploadBaseUrl = () =>
    trimTrailingSlash(import.meta.env.VITE_MINI_DRAMA_ASSET_BASE_URL || '');

/** CDN 前缀：写入配置 / 客户端访问（dev&test: static.actqa.com，prod: static.story.fun） */
export const getMiniDramaCdnBaseUrl = () =>
    trimTrailingSlash(import.meta.env.VITE_MINI_DRAMA_CDN_BASE_URL || '');

const getKnownAssetBases = () =>
    [getMiniDramaCdnBaseUrl(), getMiniDramaUploadBaseUrl(), import.meta.env.VITE_WEB2_ASSETS || '']
        .map((base) => trimTrailingSlash(base))
        .filter(Boolean);

export const normalizeMiniDramaAssetPath = (path: string) => path.replace(/^\//, '');

/**
 * 从 path 或任意已知前缀的完整 URL 中提取对象 key
 * 例：admin/2026/06/24/xxx.jpg
 */
export const extractMiniDramaAssetPath = (url?: string) => {
    if (!url) return '';
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
        return normalizeMiniDramaAssetPath(url);
    }

    for (const base of getKnownAssetBases()) {
        if (url.startsWith(base)) {
            return url.slice(base.length).replace(/^\//, '');
        }
    }

    try {
        return new URL(url).pathname.replace(/^\//, '');
    } catch {
        return url;
    }
};

/** 拼 CDN 完整 URL，写入 banner 配置 */
export const resolveMiniDramaCdnUrl = (urlOrPath?: string) => {
    if (!urlOrPath) return '';

    if (urlOrPath.startsWith('http://') || urlOrPath.startsWith('https://')) {
        const cdnBase = getMiniDramaCdnBaseUrl();
        if (cdnBase && urlOrPath.startsWith(cdnBase)) {
            return urlOrPath;
        }
        const path = extractMiniDramaAssetPath(urlOrPath);
        return cdnBase ? `${cdnBase}/${path}` : urlOrPath;
    }

    const cdnBase = getMiniDramaCdnBaseUrl();
    if (!cdnBase) return normalizeMiniDramaAssetPath(urlOrPath);
    return `${cdnBase}/${normalizeMiniDramaAssetPath(urlOrPath)}`;
};

/** 后台编辑态展示：完整 URL 原样；相对 path 走 CDN。dev 下图床走本地代理 */
export const resolveMiniDramaDisplayUrl = (urlOrPath?: string) => {
    if (!urlOrPath) return '';
    if (urlOrPath.startsWith('http://') || urlOrPath.startsWith('https://')) {
        return resolveDevImageDisplayUrl(urlOrPath);
    }
    return resolveDevImageDisplayUrl(resolveMiniDramaCdnUrl(urlOrPath));
};

/** 上传完成后拼 S3 直链（与预签名 url 去参一致，便于立刻反显） */
export const resolveMiniDramaUploadUrl = (path: string) => {
    const uploadBase = getMiniDramaUploadBaseUrl();
    const normalizedPath = normalizeMiniDramaAssetPath(path);
    if (!uploadBase) return normalizedPath;
    return `${uploadBase}/${normalizedPath}`;
};
